import { useState, useMemo, useEffect } from "react";
import { KeycloakTokenParsed } from "keycloak-js";
import { toast } from 'react-toastify';

import { createProjectItem, updateProjectItem, deleteProjectItem } from "../../api/projects";


import { useProjectRooms } from '../../hooks/useProjectRooms';
import { useUsersPage } from "../../state/users";
import { useAuth } from '../../hooks/useAuth';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { TabProjectPlanDocumentAttachment } from './TabProjectPlanDocumentAttachment';
import { TaskAssetAttachment } from '../../components/TaskAssetAttachment';
import { PhaseSelectBadge } from './PhaseSelectBadge';
import { Avatar } from '../Avatar';
import { SelectUserAvatar } from '../SelectUserAvatar';
import { AddProjectItemModal } from './AddProjectItemModal';
import { PhaseManager } from './PhaseManager';
// import { ProjectItemAssets } from './ProjectItemAssets';


import { 
  FaPlus,
  FaSearch,
  FaFilter,
  FaChevronDown,
  FaCircle,
  FaMapMarkerAlt,
  FaTrash
} from 'react-icons/fa';

import { getTableHeaderStyle, getFormLabelStyle, getTaskNameStyle, getRiskColor, getFormSelectStyle, getFormSelectContainerStyle, getLocationSelectStyle, getOriginalStatusSelectStyle, getOriginalAssigneeContainerStyle, getOriginalStatusColor, getBadgeTailwindClasses, DesignSystem } from '../../styles/designSystem';
import { RISK_OPTIONS } from '../../constants/project';

interface TabProjectPlanProps {
  projectId: string | undefined;
  token: string | undefined;
  projectData: ProjectInterface,
  tokenParsed: KeycloakTokenParsed | undefined;
  onProjectUpdate?: () => Promise<void>;
  highlightedItemId?: string | null;
  initialRiskFilter?: string;
}

  // Task row component
  const TaskRow = ({ 
  project, 
  projectData, 
  onSubmit, 
  onDelete,
  rooms, 
  onProjectUpdate,
  isNew = false,
  isHighlighted = false,
  isAssetsEnabled = true,
}: { 
  project: ProjectItemInterface, 
  projectData: ProjectInterface, 
  onSubmit: Function,
  onDelete: (projectItemId: string) => void,
  rooms: import('../../api/dataCenter').RoomLocation[],
  onProjectUpdate?: () => Promise<void>,
  isNew?: boolean,
  isHighlighted?: boolean,
  isAssetsEnabled?: boolean
}) => {
  const [form, setForm] = useState({ ...project });
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [dateValidationError, setDateValidationError] = useState<string | null>(null);
  // Asset loading moved to TaskAssetAttachment component


  const { addUserToProject } = useUsersPage();

  // Asset loading moved to TaskAssetAttachment component

  useEffect(() => {
    setForm({ ...project });
    // Check date validation on data load
    if (project.start_date && project.due_date) {
      validateDates(project.start_date, project.due_date);
    }
    // Asset loading moved to TaskAssetAttachment component
  }, [project]);

  const handleCreatePhase = async (phaseId: string) => {
    console.log('Phase created with ID:', phaseId);
    handleFieldChange('phase_id', phaseId);
  };

  // Date validation function
  const validateDates = (startDate: string, dueDate: string) => {
    if (!startDate || !dueDate) {
      setDateValidationError(null);
      return true;
    }

    const start = new Date(startDate);
    const due = new Date(dueDate);

    // Check if dates are valid
    if (isNaN(start.getTime()) || isNaN(due.getTime())) {
      setDateValidationError('Please enter valid dates');
      return false;
    }

    // Define reasonable date bounds (e.g., 10 years ago to 10 years in the future)
    const now = new Date();
    const minDate = new Date(now.getFullYear() - 10, now.getMonth(), now.getDate());
    const maxDate = new Date(now.getFullYear() + 10, now.getMonth(), now.getDate());

    // Check if dates are within reasonable bounds
    if (start < minDate || start > maxDate || due < minDate || due > maxDate) {
      setDateValidationError('Please enter valid dates within the last 10 years to the next 10 years');
      return false;
    }

    if (due < start) {
      setDateValidationError('Due date cannot be earlier than start date');
      return false;
    }

    setDateValidationError(null);
    return true;
  };





  // Function to save changes
  const saveChanges = async (updatedForm: any) => {
    // Check date validation before saving
    const isValid = validateDates(updatedForm.start_date, updatedForm.due_date);
    if (!isValid) {
      return; // Don't save if dates are invalid
    }

    setIsSaving(true);
    try {
      await onSubmit(updatedForm);
    } finally {
      setIsSaving(false);
    }
  };

  const handleFieldChange = (field: string, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
    
    // Auto-save for certain fields immediately
    if (['status', 'priority', 'assigned_to', 'owner_id', 'risks', 'location', 'phase_id', 'start_date', 'due_date'].includes(field)) {
      const updatedForm = { ...form, [field]: value };
      saveChanges(updatedForm);
    }
  };

  // Function to handle text field blur events
  const handleFieldBlur = (field: string, value: any) => {
    const updatedForm = { 
      ...form, 
      [field]: value 
    };
    
    // Synchronize title and name
    if (field === 'title') {
      updatedForm.name = value;
    } else if (field === 'name') {
      updatedForm.title = value;
    }
    
    setForm(updatedForm);
    saveChanges(updatedForm);
  };

  const handleUserAdd = async (field: 'owner_id' | 'assigned_to', userId: string) => {
    const user = projectData?.users?.find(u => u.id === userId);
    if (user) {
      const userInProject = !!projectData.users?.find((u: ProjectUserInterface) => u.id === userId);
      if (!userInProject) {
        await addUserToProject(projectData.id || '', user);
        // FIX: Don't reload project to avoid conflicts
        // await loadProjectById(projectData.id || '');
      }
      handleFieldChange(field, userId);
      // Remove await for handleSave since handleFieldChange already triggers saving
    }
  };









  return (
    <div className={`group hover:bg-gray-50/50 transition-all duration-200 ${
      isNew ? 'bg-blue-50/30' : ''
    } ${
      isHighlighted ? 'bg-yellow-50/50 border-l-4 border-yellow-400' : ''
    }`}>
      {/* Main Row */}
      <div className="flex items-center px-6 py-4 relative" style={{ borderBottom: '0.5px solid #E5E7EB' }}>
        {/* Clickable Area for Expand */}
        <div 
          className="absolute inset-0 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        />
        
        {/* Expand Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          className="mr-4 p-1 rounded-md hover:bg-gray-100 transition-colors relative z-10"
        >
          <FaChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </button>

        {/* Risk Level Indicator */}
        <div className="mr-4 relative z-10 flex items-center space-x-2">
          <div className="relative">
            {/* Rotating Ring around risk dot - when saving any field */}
            {isSaving && (
              <div className={`absolute inset-0 w-5 h-5 -ml-1.5 -mt-1.5 rounded-full border-2 border-transparent animate-spin ${
                form.risks?.toLowerCase() === 'high' ? 'border-t-red-500/60 border-r-red-500/60' :
                form.risks?.toLowerCase() === 'medium' ? 'border-t-yellow-500/60 border-r-yellow-500/60' :
                form.risks?.toLowerCase() === 'low' ? 'border-t-green-500/60 border-r-green-500/60' :
                'border-t-gray-300/60 border-r-gray-300/60'
              }`} style={{ animation: 'spin 3s linear infinite' }} />
            )}
            {/* Risk Level Dot */}
            <FaCircle 
              className="w-2 h-2" 
              style={{ color: getRiskColor(form.risks || '') }}
            />
          </div>
        </div>

        {/* Task Name */}
        <div className="flex-1 min-w-0 relative z-10 flex items-center">
          <input
            type="text"
            value={form.name || ''}
            onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
            onBlur={(e) => handleFieldBlur('name', e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            className="flex-1 bg-transparent border-0 focus:outline-none placeholder-gray-400"
            style={getTaskNameStyle()}
            placeholder="Task name..."
          />

        </div>

        {/* Phase */}
        <div className="mx-6 w-20 relative z-10">
          <PhaseSelectBadge
            phases={projectData?.phases || []}
            selectedPhaseId={form.phase_id || ''}
            projectId={project.project_id}
            onChange={(phaseId: string) => {
              handleFieldChange('phase_id', phaseId);
            }}
            onCreate={handleCreatePhase}
            onProjectUpdate={onProjectUpdate}
          />
        </div>

        {/* Status */}
        <div className="mx-6 w-24 relative z-10 flex items-center">
          <select
            value={form.status || ''}
            onChange={(e) => {
              handleFieldChange('status', e.target.value);
            }}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            className={`${getOriginalStatusColor(form.status)}`}
            style={getOriginalStatusSelectStyle()}
          >
            <option value="NOT_STARTED">Not Started</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="ON_HOLD">On Hold</option>
            <option value="COMPLETED">Completed</option>
          </select>

        </div>

        {/* Owner */}
        <div className="mx-6 w-32 relative z-10" style={getOriginalAssigneeContainerStyle()}>
          {(() => {
            const assignedUser = projectData?.users?.find(user => user.id === form.owner_id);
            if (assignedUser) {
              return (
                <Avatar
                  firstName={assignedUser.first_name}
                  lastName={assignedUser.last_name}
                  email={assignedUser.email}
                  size={20}
                  className="mr-2 flex-shrink-0"
                />
              );
            } else {
              return (
                <SelectUserAvatar
                  size={20}
                  className="mr-2 flex-shrink-0"
                />
              );
            }
          })()}
          <select
            value={form.owner_id || ''}
            onChange={(e) => handleUserAdd('owner_id', e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            style={{ ...getOriginalStatusSelectStyle(), flex: 1, minWidth: 0 }}
          >
            <option value="">Unassigned</option>
            {projectData?.users?.map((user) => (
              <option key={user.id} value={user.id}>
                {user.first_name} {user.last_name}
              </option>
            ))}
          </select>

        </div>

        {/* Due Date */}
        <div className="mx-6 w-24 flex items-center text-xs text-gray-600 relative z-10">
          <input
            type="date"
            value={form.due_date ? new Date(form.due_date).toISOString().split('T')[0] : ''}
            onChange={(e) => {
              const value = e.target.value ? new Date(e.target.value).toISOString() : '';
              handleFieldChange('due_date', value);
            }}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            min={(() => {
              const now = new Date();
              const minDate = new Date(now.getFullYear() - 10, now.getMonth(), now.getDate());
              return minDate.toISOString().split('T')[0];
            })()}
            max={(() => {
              const now = new Date();
              const maxDate = new Date(now.getFullYear() + 10, now.getMonth(), now.getDate());
              return maxDate.toISOString().split('T')[0];
            })()}
            className="bg-transparent border-0 focus:outline-none text-xs w-full"
          />
        </div>

        {/* Progress */}
        <div className="mx-6 w-20 relative z-10">
          <div className="flex items-center space-x-2">
            <div className="w-8 bg-gray-200 rounded-full h-1.5">
              <div 
                className="bg-indigo-600 h-1.5 rounded-full transition-all duration-300" 
                style={{ width: `${form.completion_percentage || 0}%` }}
              ></div>
            </div>
            <span className="text-xs text-gray-500">{form.completion_percentage || 0}%</span>
          </div>
        </div>

        {/* Assets */}
        {isAssetsEnabled && (
          <div className="mx-6 w-20 relative z-10">
            <TaskAssetAttachment
              projectId={project.project_id}
              itemId={project.id}
              onUpdate={onProjectUpdate}
            />
          </div>
        )}

        {/* Documents */}
        <div className="ml-6 w-32 relative z-10">
                      <TabProjectPlanDocumentAttachment
              projectId={project.project_id}
              itemId={project.id}
              documentUrl={project.document}
              fileName={project.file_name}
            />
        </div>




      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="px-6 pb-6 pt-2 bg-gray-50/30" style={{ borderTop: '0.5px solid #E5E7EB' }}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            
            {/* Title and Description */}
            <div className="lg:col-span-2 space-y-4">
              {/* Title */}
              <div className="pt-2">
                <label className="block mb-2" style={getFormLabelStyle()}>Title</label>
                <input
                  type="text"
                  value={form.title || form.name || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value, name: e.target.value }))}
                  onBlur={(e) => {
                    handleFieldBlur('title', e.target.value);
                    handleFieldBlur('name', e.target.value);
                  }}
                  className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  style={getTaskNameStyle()}
                  placeholder={form.name || "Add title..."}
                />
              </div>
              
              {/* Description */}
              <div>
                <label className="block mb-2" style={getFormLabelStyle()}>Description</label>
                <textarea
                  value={form.description || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                  onBlur={(e) => handleFieldBlur('description', e.target.value)}
                  className="w-full text-sm text-gray-700 bg-white border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  placeholder="Add description..."
                  rows={6}
                />
              </div>
            </div>

            {/* Project Details */}
            <div className="space-y-4">
              {/* Location */}
              <div className="pt-2">
                <label className="block mb-2" style={getFormLabelStyle()}>Location</label>
                <div className="flex items-center text-sm text-gray-700 bg-white border border-gray-200 rounded-lg px-3 py-2">
                  <FaMapMarkerAlt className="w-3 h-3 mr-2 text-gray-400" />
                  <select
                    value={form.location?.room_id || ''}
                    onChange={(e) => {
                      const roomId = e.target.value;
                      handleFieldChange('location', roomId ? { room_id: roomId } : null);
                    }}
                    style={getLocationSelectStyle()}
                  >
                    <option value="">Select location</option>
                    {rooms.map((room) => (
                      <option key={room.room_id} value={room.room_id}>
                        {room.room_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Owner */}
              <div>
                <label className="block mb-2" style={getFormLabelStyle()}>Owner</label>
                <div style={getFormSelectContainerStyle()}>
                  <select
                    value={form.owner_id || ''}
                    onChange={(e) => handleUserAdd('owner_id', e.target.value)}
                    style={getFormSelectStyle()}
                  >
                    <option value="">Unassigned</option>
                    {projectData?.users?.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Assignee */}
              <div>
                <label className="block mb-2" style={getFormLabelStyle()}>Assignee</label>
                <div style={getFormSelectContainerStyle()}>
                  <select
                    value={form.assigned_to || ''}
                    onChange={(e) => handleUserAdd('assigned_to', e.target.value)}
                    style={getFormSelectStyle()}
                  >
                    <option value="">Unassigned</option>
                    {projectData?.users?.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Priority */}
              <div>
                <label className="block mb-2" style={getFormLabelStyle()}>Priority</label>
                <div style={getFormSelectContainerStyle()}>
                  <select
                    value={form.priority || ''}
                    onChange={(e) => {
                      handleFieldChange('priority', e.target.value);
                    }}
                    style={getFormSelectStyle()}
                  >
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                  </select>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block mb-2" style={getFormLabelStyle()}>Start Date</label>
                  <input
                    type="date"
                    value={form.start_date ? new Date(form.start_date).toISOString().split('T')[0] : ''}
                    onChange={(e) => {
                      const value = e.target.value ? new Date(e.target.value).toISOString() : '';
                      const updatedForm = { ...form, start_date: value };
                      setForm(updatedForm);
                      
                      // Validate dates on change
                      validateDates(value, form.due_date);
                      
                      // Save only if validation passes successfully
                      if (validateDates(value, form.due_date)) {
                      handleFieldChange('start_date', value);
                      }
                    }}
                    min={(() => {
                      const now = new Date();
                      const minDate = new Date(now.getFullYear() - 10, now.getMonth(), now.getDate());
                      return minDate.toISOString().split('T')[0];
                    })()}
                    max={(() => {
                      const now = new Date();
                      const maxDate = new Date(now.getFullYear() + 10, now.getMonth(), now.getDate());
                      return maxDate.toISOString().split('T')[0];
                    })()}
                    className={`w-full text-sm text-gray-700 bg-white border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 ${
                      dateValidationError 
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                        : 'border-gray-200 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                  />
                </div>
                <div>
                  <label className="block mb-2" style={getFormLabelStyle()}>Due Date</label>
                  <input
                    type="date"
                    value={form.due_date ? new Date(form.due_date).toISOString().split('T')[0] : ''}
                    onChange={(e) => {
                      const value = e.target.value ? new Date(e.target.value).toISOString() : '';
                      const updatedForm = { ...form, due_date: value };
                      setForm(updatedForm);
                      
                      // Validate dates on change
                      validateDates(form.start_date, value);
                      
                      // Save only if validation passes successfully
                      if (validateDates(form.start_date, value)) {
                      handleFieldChange('due_date', value);
                      }
                    }}
                    min={(() => {
                      const now = new Date();
                      const minDate = new Date(now.getFullYear() - 10, now.getMonth(), now.getDate());
                      return minDate.toISOString().split('T')[0];
                    })()}
                    max={(() => {
                      const now = new Date();
                      const maxDate = new Date(now.getFullYear() + 10, now.getMonth(), now.getDate());
                      return maxDate.toISOString().split('T')[0];
                    })()}
                    className={`w-full text-sm text-gray-700 bg-white border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 ${
                      dateValidationError 
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                        : 'border-gray-200 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                  />
                </div>
              </div>

              {/* Date Validation Error */}
              {dateValidationError && (
                <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-4 h-4 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-red-700 font-medium">{dateValidationError}</span>
                  </div>
                  <p className="text-xs text-red-600 mt-1 ml-6">
                    Please adjust the dates so that the due date is on or after the start date.
                  </p>
                </div>
              )}

              {/* Risk */}
              <div>
                <label className="block mb-2" style={getFormLabelStyle()}>Risk Level</label>
                <div style={getFormSelectContainerStyle()}>
                  <select
                    value={form.risks?.toLowerCase() || 'none'}
                    onChange={(e) => {
                      handleFieldChange('risks', e.target.value);
                    }}
                    style={getFormSelectStyle()}
                  >
                    {RISK_OPTIONS.map(option => (
                      <option key={option.value} value={option.value.toLowerCase()}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Progress */}
              <div>
                <label className="block mb-2" style={getFormLabelStyle()}>Progress %</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={form.completion_percentage || ''}
                  onChange={(e) => {
                    // Only update local state, don't save
                    const value = Math.min(Math.max(parseInt(e.target.value) || 0, 0), 100);
                    setForm(prev => ({ ...prev, completion_percentage: value }));
                  }}
                  onBlur={(e) => {
                    // Save only on blur
                    const value = Math.min(Math.max(parseInt(e.target.value) || 0, 0), 100);
                    handleFieldBlur('completion_percentage', value);
                  }}
                  className="w-full text-sm text-gray-700 bg-white border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0"
                />
              </div>

              {/* Actions */}
              <div className="pt-2">
                <button
                  onClick={() => onDelete(project.id!)}
                  className="flex items-center space-x-2 px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <FaTrash className="w-3 h-3" />
                  <span>Delete Task</span>
                </button>
              </div>
            </div>
          </div>

          {/* Assets Section - Temporarily disabled until API is fixed */}
          {/*
          <div className="mt-6 pt-6 border-t border-gray-200">
            <ProjectItemAssets
              projectId={project.project_id}
              itemId={project.id}
              onUpdate={onProjectUpdate}
            />
          </div>
          */}
        </div>
      )}
    </div>
  );
};

export const TabProjectPlan = (
  { token, projectData, projectId, onProjectUpdate, highlightedItemId, initialRiskFilter }: TabProjectPlanProps
) => {
  const { handleAuthError } = useAuth();
  const { isFeatureEnabled } = useFeatureFlag();
  // Add cleanup flag to prevent DOM operations after unmount
  const [isUnmounted, setIsUnmounted] = useState(false);
  const [newRows, setNewRows] = useState<ProjectItemInterface[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [userFilter, setUserFilter] = useState<string>('all');
  const [phaseFilter, setPhaseFilter] = useState<string>('all');
  const [riskFilter, setRiskFilter] = useState<string>(initialRiskFilter || 'all');
  const [roomFilter, setRoomFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [showPhaseManager, setShowPhaseManager] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Scroll detection
  useEffect(() => {
    if (isUnmounted) return;

    const handleScroll = () => {
      if (!isUnmounted) {
      setIsScrolled(window.scrollY > 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isUnmounted]);

  // Cleanup effect to prevent DOM operations after unmount
  useEffect(() => {
    return () => {
      setIsUnmounted(true);
    };
  }, []);


  // Load rooms
  const { rooms } = useProjectRooms(projectId || '');

  // Filter tasks
  const filteredTasks = useMemo(() => {
    if (!projectData?.items) return [];
    
    return projectData.items.filter((task) => {
      // Search filter
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        if (
          !task.name?.toLowerCase().includes(searchLower) &&
          !task.description?.toLowerCase().includes(searchLower)
        ) {
          return false;
        }
      }

      // Status filter
      if (statusFilter !== 'all' && task.status !== statusFilter) {
        return false;
      }

      // User filter
      if (userFilter !== 'all' && task.assigned_to !== userFilter) {
        return false;
      }

      // Phase filter
      if (phaseFilter !== 'all' && task.phase_id !== phaseFilter) {
        return false;
      }

      // Risk filter
      if (riskFilter !== 'all' && task.risks !== riskFilter) {
        return false;
      }

      // Room filter
      if (roomFilter !== 'all' && task.location?.room_id !== roomFilter) {
        return false;
      }
      
      return true;
    });
  }, [projectData?.items, searchQuery, statusFilter, userFilter, phaseFilter, riskFilter, roomFilter]);

  const updateProjectItemHandler = async (form: ProjectItemInterface) => {
    console.log('updateProjectItemHandler called with:', form);
    if (token && projectId) {
      try {
        console.log('🔐 Token check:', {
          hasToken: !!token,
          tokenLength: token?.length || 0,
          projectId: projectId
        });

        await updateProjectItem(token, projectId, form.id, form);
        console.log('updateProjectItem API call successful');
        
        // FIX: Remove project reload to prevent tasks from closing
        // if (onProjectUpdate) {
        //   console.log('Calling onProjectUpdate to refresh data...');
        //   await onProjectUpdate();
        //   console.log('onProjectUpdate completed');
        // }
        
        console.log('Project item saved successfully');
        toast.success('Project item updated successfully');
      } catch (error) {
        console.error('Error in updateProjectItemHandler:', error);
        
        // Check if this is an authentication error
        if (error instanceof Error && (error as any).isAuthError) {
          console.error('🔐 Authentication error detected in updateProjectItemHandler');
          toast.error('Your session has expired. Please refresh the page and try again.');
          
          // Redirect to login after a short delay
          setTimeout(() => {
            window.location.href = '/';
          }, 2000);
          return;
        }
        
        // Try to handle authentication error and retry
        const shouldRetry = await handleAuthError(error);
        if (shouldRetry && token && projectId) {
          console.log('🔄 Retrying updateProjectItem after token refresh...');
          try {
            await updateProjectItem(token, projectId, form.id, form);
            console.log('✅ Retry successful');
            toast.success('Project item updated successfully');
            return;
          } catch (retryError) {
            console.error('❌ Retry failed:', retryError);
            toast.error('Failed to save changes after retry');
            throw retryError;
          }
        }
        
        // Handle other errors
        if (error instanceof Error) {
          toast.error(error.message || 'Failed to save changes');
        } else {
          toast.error('Failed to save changes');
        }
        
        throw error; // Re-throw error for handleSave
      } finally {
        // Loading removed
      }
    } else {
      console.error('Missing token or projectId:', { token: !!token, projectId });
      toast.error('Authentication required. Please refresh the page.');
      throw new Error('Missing authentication or project ID');
    }
  }

  const createProjectItemHandler = async (idx: number, form: ProjectItemInterface) => {
    if (token && projectId) {
      try {
        await createProjectItem(token, projectId, form);
        console.log('createProjectItem API call successful');
        
        setNewRows(newRows.filter((_, i) => i !== idx));
        
        // Update project data to reflect changes
        if (onProjectUpdate) {
          await onProjectUpdate();
        }
        
        console.log('New project item created successfully');
        toast.success('New project item created successfully');
      } catch (error) {
        console.error('Error in createProjectItemHandler:', error);
        toast.error('Failed to create new project item');
      }
    }
  }

  const handleAddTask = async (itemData: any) => {
    if (token && projectId) {
      try {
        console.log('Creating new task:', itemData);
        await createProjectItem(token, projectId, itemData);
        
        // Refresh project data
        if (onProjectUpdate) {
          await onProjectUpdate();
        }
        
        console.log('Task created successfully');
        toast.success('Task created successfully');
      } catch (error) {
        console.error('Error creating task:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to create task';
        toast.error(errorMessage);
        throw error;
      }
    }
  };

  const deleteProjectItemHandler = async (projectItemId: string) => {
    if (!token || !projectId) return;
    
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    
    try {
      await deleteProjectItem(token, projectId, projectItemId);
      toast.success('Task deleted successfully');
      
      if (onProjectUpdate) {
        await onProjectUpdate();
      }
    } catch (error: any) {
      console.error('Error deleting task:', error);
      toast.error(error.message || 'Failed to delete task');
    }
  };



  const getStatusCount = (status: string) => {
    return projectData?.items?.filter(item => item.status === status).length || 0;
  };

  // Function to handle project update after phase changes
  const handlePhasesUpdate = async () => {
    if (onProjectUpdate) {
      await onProjectUpdate();
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header with Search, Actions and Table Header - Fixed Sticky Section */}
      <div className="sticky top-0 z-30">
        <div className="max-w-[1280px] mx-auto px-0 py-4">
          <div className={`bg-white rounded-lg transition-shadow duration-200 ${isScrolled ? 'shadow-md' : ''}`} style={{ border: `0.5px solid ${DesignSystem.colors.border.gray200}` }}>
          {/* Search and Actions Row */}
          <div className="px-6 py-4" style={{ borderBottom: '0.5px solid #E5E7EB' }}>
          <div className="flex items-center justify-between">
            {/* Left side - Search and Quick Stats */}
            <div className="flex items-center space-x-6">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search tasks..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2.5 w-80 text-sm bg-gray-50 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                />
              </div>
              
              {/* Quick Stats */}
              <div className="flex items-center space-x-4 text-sm">
                <span className="text-gray-500">
                  {filteredTasks.length} of {projectData?.items?.length || 0} tasks
                </span>
                <div className="w-px h-4 bg-gray-300"></div>
                <span className="text-gray-500">
                  {getStatusCount('COMPLETED')} completed
                </span>
              </div>
            </div>

            {/* Right side - Actions */}
            <div className="flex items-center space-x-3">
              {/* Filters */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center space-x-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${
                  showFilters 
                    ? 'bg-blue-50 text-blue-600 border border-blue-200' 
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-transparent'
                }`}
              >
                <FaFilter className="w-3.5 h-3.5" />
                <span>Filter</span>
                {(statusFilter !== 'all' || userFilter !== 'all' || phaseFilter !== 'all' || riskFilter !== 'all' || roomFilter !== 'all') && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-600 text-white rounded-full">
                    {[statusFilter, userFilter, phaseFilter, riskFilter, roomFilter].filter(f => f !== 'all').length}
                  </span>
                )}
              </button>

              {/* Manage Phases */}
              <button
                onClick={() => setShowPhaseManager(true)}
                className="flex items-center space-x-2 bg-transparent text-gray-400 border border-gray-200 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-colors"
              >
                <FaPlus className="w-3.5 h-3.5" />
                <span>Manage Phases</span>
              </button>

              {/* Add Task */}
                <button
                  onClick={() => setShowAddTaskModal(true)}
                  className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm"
                >
                  <FaPlus className="w-3.5 h-3.5" />
                  <span>Add Task</span>
                </button>
            </div>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="mt-4 pt-4" style={{ borderTop: '0.5px solid #E5E7EB' }}>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                <div style={getFormSelectContainerStyle()}>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={getFormSelectStyle()}
                  >
                    <option value="all">All Status</option>
                    <option value="NOT_STARTED">Not Started</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="COMPLETED">Completed</option>
                  </select>
                </div>
                <div style={getFormSelectContainerStyle()}>
                  <select
                    value={userFilter}
                    onChange={(e) => setUserFilter(e.target.value)}
                    style={getFormSelectStyle()}
                  >
                      <option value="all">All Owners</option>
                    {projectData?.users?.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={getFormSelectContainerStyle()}>
                  <select
                    value={phaseFilter}
                    onChange={(e) => setPhaseFilter(e.target.value)}
                    style={getFormSelectStyle()}
                  >
                    <option value="all">All Phases</option>
                    {projectData?.phases?.map((phase) => (
                      <option key={phase.id} value={phase.id}>
                        {phase.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={getFormSelectContainerStyle()}>
                  <select
                    value={riskFilter}
                    onChange={(e) => setRiskFilter(e.target.value)}
                    style={getFormSelectStyle()}
                  >
                    <option value="all">All Priority</option>
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                  </select>
                </div>
                <div style={getFormSelectContainerStyle()}>
                  <select
                    value={roomFilter}
                    onChange={(e) => setRoomFilter(e.target.value)}
                    style={getFormSelectStyle()}
                  >
                    <option value="all">All Locations</option>
                    {rooms?.map((room) => (
                      <option key={room.room_id} value={room.room_id}>
                        {room.room_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              {/* Active Filters & Clear */}
              {(statusFilter !== 'all' || userFilter !== 'all' || phaseFilter !== 'all' || riskFilter !== 'all' || roomFilter !== 'all') && (
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500">Active filters:</span>
                    {statusFilter !== 'all' && (
                      <span className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-md">
                        Status: {statusFilter.replace('_', ' ')}
                        <button onClick={() => setStatusFilter('all')} className="ml-1 text-blue-500 hover:text-blue-700">×</button>
                      </span>
                    )}
                    {userFilter !== 'all' && (
                      <span className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-md">
                        User: {projectData?.users?.find(u => u.id === userFilter)?.first_name}
                        <button onClick={() => setUserFilter('all')} className="ml-1 text-blue-500 hover:text-blue-700">×</button>
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setStatusFilter('all');
                      setUserFilter('all');
                      setPhaseFilter('all');
                      setRiskFilter('all');
                      setRoomFilter('all');
                    }}
                    className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>
          )}
      </div>

          {/* Table Header */}
          <div className="px-6 py-2">
            <div className="flex items-center text-xs font-medium" style={getTableHeaderStyle()}>
              <div className="w-8"></div> {/* Expand button space */}
              <div className="w-4 mr-4"></div> {/* Risk indicator space */}
              <div className="flex-1" role="columnheader">Task</div>
              <div className="mx-6 w-20" role="columnheader">Phase</div>
              <div className="mx-6 w-24" role="columnheader">Status</div>
              <div className="mx-6 w-32" role="columnheader">Owner</div>
              <div className="mx-6 w-24" role="columnheader">Due</div>
              <div className="mx-6 w-20" role="columnheader">Progress</div>
              {isFeatureEnabled('ASSETS_TAB') && (
                <div className="mx-6 w-20" role="columnheader">
                  Assets <span className={getBadgeTailwindClasses('dev')}>dev</span>
                </div>
              )}
              <div className="ml-6 w-32" role="columnheader">Docs</div>
            </div>
          </div>
          </div>
            </div>
          </div>

      {/* Task List - Scrollable Content */}
      <div className="max-w-[1280px] mx-auto px-0 -mt-4">
        <div className="bg-white">

          {/* Task Rows */}
          <div>
            {filteredTasks.sort((a: ProjectItemInterface, b: ProjectItemInterface) => a.id > b.id ? 1 : -1).map((project) => (
              <TaskRow 
                key={project.id} 
                project={project} 
                projectData={projectData}
                onSubmit={updateProjectItemHandler}
                onDelete={deleteProjectItemHandler}
                rooms={rooms}
                onProjectUpdate={onProjectUpdate}
                isHighlighted={highlightedItemId === project.id}
                isAssetsEnabled={isFeatureEnabled('ASSETS_TAB')}
              />
            ))}
            
            {newRows.map((project: ProjectItemInterface, index) => (
              <TaskRow 
                key={index} 
                project={project} 
                projectData={projectData}
                onSubmit={(form: ProjectItemInterface) => createProjectItemHandler(index, form)}
                onDelete={deleteProjectItemHandler}
                rooms={rooms}
                onProjectUpdate={onProjectUpdate}
                isNew={true}
                isHighlighted={highlightedItemId === project.id}
                isAssetsEnabled={isFeatureEnabled('ASSETS_TAB')}
              />
            ))}
          </div>
        </div>

        {/* Empty State */}
        {filteredTasks.length === 0 && newRows.length === 0 && (
          <div className="text-center py-16">
            <div className="text-gray-400 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h3 className="text-lg font-light text-gray-900 mb-2">No tasks found</h3>
            <p className="text-gray-600 font-light mb-8">
              {searchQuery || statusFilter !== 'all' || userFilter !== 'all'
                ? 'Try adjusting your search or filters.'
                : 'Get started by creating your first task.'
              }
            </p>
              <button
                onClick={() => setShowAddTaskModal(true)}
                className="bg-white border border-gray-200 text-gray-600 px-6 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-colors"
              >
                Create First Task
              </button>
          </div>
        )}
      </div>

      {/* Add Task Modal */}
      {showAddTaskModal && projectId && (
        <AddProjectItemModal
          isOpen={showAddTaskModal}
          onClose={() => setShowAddTaskModal(false)}
          onAddItem={handleAddTask}
          projectData={projectData}
          users={projectData?.users || []}
        />
      )}

      {/* Phase Manager Modal */}
      {showPhaseManager && projectId && (
        <PhaseManager
          token={token}
          projectId={projectId}
          phases={projectData?.phases || []}
          onClose={() => setShowPhaseManager(false)}
          onPhasesUpdate={handlePhasesUpdate}
        />
      )}
    </div>
  );
}; 